import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import {
  AdPlacement,
  type AdType,
  Brand,
  debugLog,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';
import { isMiddleBannerOverlappingFilters } from '@client/core/utils/dom/handleAdsOverlap/isMiddleBannerOverlappingFilters.js';
import { setupAdnamiMidscrollObserverForWallpaper } from '@client/core/utils/dom/handleWallpaper.js';
import { $config } from '@client/core/atoms/config.js';

import {
  eventIsNativeCpmAd,
  sizeIsAdnami,
  sizeIsFullScreenAd,
  sizeIsNativeAd,
  sizeIsDbaDesktopIntermingle,
  sizeExceedsCenterContentWidth
} from '../util.js';

/** Fired when creative code has been injected into an ad slot. */
export function onSlotRenderEnded(
  event: googletag.events.SlotRenderEndedEvent
) {
  const placementId = event.slot.getSlotElementId() as PlacementId;

  if (event.isEmpty) {
    onSlotRenderEnded.whenEmpty(placementId, event);
    return;
  }

  const isMiddleSlot = placementId.startsWith('advt_middle_');

  const { brand, vertical } = $config.get() ?? {};
  const isDba = brand === Brand.Dba;
  // Should be Vertical.Car, but we don't have time now to update advertising-shared/src/types/api/api.autogenerated.ts
  // We shall either remove this completely (AD-234 is temporary solution), or properly update api.autogenerated.ts
  const isCar = vertical?.toString() === 'car';

  if (
    isMiddleSlot &&
    onSlotRenderEnded.shouldCollapseMiddleAd(event.size, placementId) &&
    // AD-234: Temporary fix, to be removed
    !(isDba && isCar)
  ) {
    onSlotRenderEnded.whenMiddleAdCollided(placementId, event);
    return;
  }

  const creativeType = onSlotRenderEnded.getCreativeType(
    event,
    isMiddleSlot,
    placementId
  );

  if (creativeType === 'adnami' && placementId === AdPlacement.AdvtMiddle2.id) {
    setupAdnamiMidscrollObserverForWallpaper();
  }

  debugLog(
    `Creative type from size ${JSON.stringify(event.size)}: ${creativeType}`
  );

  updatePlacementKeyValueById(placementId, 'creativeType', creativeType);
  updatePlacementKeyValueById(placementId, 'status', 'loaded');
  updateMetricByKey(placementId, PlacementStatus.AdLoaded);
}

export namespace onSlotRenderEnded {
  export function whenEmpty(
    placementId: PlacementId,
    event: googletag.events.SlotRenderEndedEvent
  ) {
    debugLog(
      `Google ad did not fill for placement: ${placementId} with slot`,
      event.slot
    );

    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
  }

  export function whenMiddleAdCollided(
    placementId: PlacementId,
    event: googletag.events.SlotRenderEndedEvent
  ) {
    debugLog(
      `Google ad overlaps with search filters: ${placementId} with slot`,
      event.slot
    );

    updateMetricByKey(placementId, PlacementStatus.CollapsedOnCollision);

    updatePlacementKeyValueById(placementId, 'status', 'collapsedOnCollision');
  }

  export function getCreativeType(
    event: googletag.events.SlotRenderEndedEvent,
    isMiddleSlot: boolean,
    placementId: PlacementId
  ): AdType {
    const { size } = event;

    if (placementId === AdPlacement.AdvtAudience.id) return 'audience-pixel';

    // Check ad types in order of specificity
    if (isMiddleSlot && sizeIsDbaDesktopIntermingle(size)) {
      return 'intermingles';
    }

    if (sizeIsAdnami(size)) return 'adnami';
    if (sizeIsFullScreenAd(size)) return 'takeover';
    if (eventIsNativeCpmAd(event)) return 'sponsored-content';
    if (sizeIsNativeAd(size)) return 'native';

    return 'banner';
  }

  export function shouldCollapseMiddleAd(
    adSize: googletag.events.SlotRenderEndedEvent['size'],
    placementId: PlacementId
  ): boolean {
    return (
      sizeExceedsCenterContentWidth(adSize) &&
      isMiddleBannerOverlappingFilters(placementId)
    );
  }
}
